import React, { useState } from "react";
import { Plus, Trash2, Wand2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Reusable conversation metadata (attributes) editor.
 *
 * Props:
 *   items          – array of { name, value, auto_generate, generation_prompt }
 *   savedMetadata  – array of pre-saved metadata templates to pick from
 *   onAdd          – (item) => void   called when user confirms a new attribute
 *   onRemove       – (index) => void  called when user removes an attribute
 *   loading        – bool             disables the add button while saving
 */
const EMPTY_ITEM = { name: "", value: "", auto_generate: false, generation_prompt: "" };

export default function ConversationMetadata({
  items = [],
  savedMetadata = [],
  groups = [],
  onAdd,
  onRemove,
  onSaveAsGroup,
  onUseGroup,
  loading = false,
}) {
  const [draft, setDraft] = useState(EMPTY_ITEM);
  const [showForm, setShowForm] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showSaveGroup, setShowSaveGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);

  const handleSaveGroup = async () => {
    if (!groupName.trim() || !onSaveAsGroup || items.length === 0) return;
    try {
      setSavingGroup(true);
      await onSaveAsGroup(groupName.trim());
      setGroupName("");
      setShowSaveGroup(false);
    } finally {
      setSavingGroup(false);
    }
  };

  const handleUseGroup = (group) => {
    (group.items || []).forEach((attr) => onAdd(attr));
    // Report the picked group so callers can attach it to the record (not just
    // expand its items into the editor for the LLM to read).
    onUseGroup?.(group);
    setShowGroups(false);
  };

  const canAdd =
    draft.name.trim() &&
    (draft.value.trim() || (draft.auto_generate && draft.generation_prompt.trim()));

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({ ...draft });
    setDraft(EMPTY_ITEM);
    setShowForm(false);
  };

  const handleUseSaved = (meta) => {
    onAdd({ ...meta });
    setShowSaved(false);
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Conversation Attributes</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Dynamic values injected into the conversation at runtime
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedMetadata.length > 0 && (
            <button
              type="button"
              onClick={() => { setShowSaved((s) => !s); setShowForm(false); setShowGroups(false); }}
              className="text-sm px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              Use saved
            </button>
          )}
          {groups.length > 0 && (
            <button
              type="button"
              onClick={() => { setShowGroups((s) => !s); setShowForm(false); setShowSaved(false); }}
              className="text-sm px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              Use group
            </button>
          )}
          {onSaveAsGroup && items.length > 0 && (
            <button
              type="button"
              onClick={() => { setShowSaveGroup((s) => !s); setShowForm(false); setShowSaved(false); setShowGroups(false); }}
              className="text-sm px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
            >
              Save as group
            </button>
          )}
          <button
            type="button"
            onClick={() => { setShowForm((s) => !s); setShowSaved(false); setShowGroups(false); setShowSaveGroup(false); }}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add attribute
          </button>
        </div>
      </div>

      {/* Saved metadata picker */}
      {showSaved && (
        <div className="border-b border-border px-5 py-3 bg-blue-50">
          <p className="text-sm font-medium text-blue-700 mb-2">Pick a saved attribute</p>
          {savedMetadata.length === 0 ? (
            <p className="text-sm text-muted-foreground">No saved attributes yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {savedMetadata.map((meta) => (
                <button
                  key={meta.id}
                  type="button"
                  onClick={() => handleUseSaved(meta)}
                  className="text-sm px-3 py-1.5 rounded-full border border-blue-200 bg-card text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  {meta.name}
                  {meta.auto_generate ? " (auto)" : meta.value ? ` = ${meta.value}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Groups picker */}
      {showGroups && (
        <div className="border-b border-border px-5 py-3 bg-violet-50">
          <p className="text-sm font-medium text-violet-700 mb-2">Pick an attribute group</p>
          {groups.length === 0 ? (
            <p className="text-sm text-muted-foreground">No attribute groups yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => handleUseGroup(group)}
                  className="text-sm px-3 py-1.5 rounded-full border border-violet-200 bg-card text-violet-700 hover:bg-violet-100 transition-colors"
                >
                  {group.name}
                  <span className="ml-1 text-violet-400">({(group.items || []).length})</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save-as-group form */}
      {showSaveGroup && (
        <div className="border-b border-border px-5 py-3 bg-violet-50 flex items-center gap-2">
          <input
            type="text"
            placeholder="Group name (e.g. Checking account caller)"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-violet-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 bg-card"
          />
          <button
            type="button"
            onClick={() => { setShowSaveGroup(false); setGroupName(""); }}
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveGroup}
            disabled={!groupName.trim() || savingGroup}
            className="text-sm px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {savingGroup ? "Saving…" : `Save group (${items.length})`}
          </button>
        </div>
      )}

      {/* Inline add form */}
      {showForm && (
        <div className="border-b border-border px-5 py-4 bg-muted/50 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Attribute name</label>
              <input
                type="text"
                placeholder="e.g. customer_name"
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">
                {draft.auto_generate ? "Generation prompt" : "Value"}
              </label>
              <input
                type="text"
                placeholder={draft.auto_generate ? "e.g. a random first name" : "e.g. John"}
                value={draft.auto_generate ? draft.generation_prompt : draft.value}
                onChange={(e) =>
                  setDraft((d) =>
                    d.auto_generate
                      ? { ...d, generation_prompt: e.target.value }
                      : { ...d, value: e.target.value }
                  )
                }
                className="w-full px-3 py-1.5 text-sm border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={draft.auto_generate}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    auto_generate: e.target.checked,
                    value: e.target.checked ? "" : d.value,
                    generation_prompt: e.target.checked ? d.generation_prompt : "",
                  }))
                }
                className="h-3.5 w-3.5 rounded text-primary border-input focus:ring-primary"
              />
              <Wand2 className="h-3.5 w-3.5 text-violet-500" />
              <span className="text-sm text-muted-foreground">Auto-generate value with AI</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setDraft(EMPTY_ITEM); }}
                className="text-sm px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={!canAdd || loading}
                className="text-sm px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Saving…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {items.length === 0 ? (
        <div className="py-8 px-5">
          <p className="text-sm text-muted-foreground text-center mb-4">
            No attributes added yet. Pick from saved attributes, use a group, or create a new one.
          </p>
          {savedMetadata.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">Saved attributes</p>
              <div className="flex flex-wrap gap-2">
                {savedMetadata.map((meta) => (
                  <button
                    key={meta.id}
                    type="button"
                    onClick={() => handleUseSaved(meta)}
                    className="text-sm px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    + {meta.name}
                    {meta.auto_generate ? " (auto)" : meta.value ? ` = ${meta.value}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          {groups.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-muted-foreground mb-2">Attribute groups</p>
              <div className="flex flex-wrap gap-2">
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleUseGroup(group)}
                    className="text-sm px-3 py-1.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                  >
                    + {group.name}
                    <span className="ml-1 text-violet-400">({(group.items || []).length})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => { setShowForm(true); setShowSaved(false); setShowGroups(false); }}
              className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border border-dashed border-input text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Create new attribute
            </button>
          </div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left text-sm font-medium text-muted-foreground px-5 py-2.5 w-1/3">Attribute</th>
              <th className="text-left text-sm font-medium text-muted-foreground px-5 py-2.5">Value / Prompt</th>
              <th className="text-left text-sm font-medium text-muted-foreground px-5 py-2.5 w-24">Mode</th>
              <th className="px-5 py-2.5 w-12" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((meta, index) => (
              <tr key={index} className="group hover:bg-muted/50/60 transition-colors">
                <td className="px-5 py-3 font-medium text-foreground truncate max-w-0">{meta.name}</td>
                <td className="px-5 py-3 text-muted-foreground truncate max-w-0">
                  {meta.auto_generate
                    ? <span className="text-violet-600">{meta.generation_prompt || "—"}</span>
                    : meta.value || "—"}
                </td>
                <td className="px-5 py-3">
                  {meta.auto_generate ? (
                    <span className="inline-flex items-center gap-1 text-sm px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">
                      <Wand2 className="h-3 w-3" />
                      AI
                    </span>
                  ) : (
                    <span className="inline-flex text-sm px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                      Fixed
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(index)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
